import areasService from '@/services/areas';
import collaboratorsService from '@/services/collaborators';
import currenciesService from '@/services/currencies';
import customersService from '@/services/customers';
import expenseCategoriesService from '@/services/expenseCategories';
import expenseClassesService from '@/services/expenseClasses';
import feesService from '@/services/fees';
import suppliersService from '@/services/suppliers';
import tagsService from '@/services/tags';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import {
  buildExpenseFeeHasXmlOptions,
  normalizeExpenseFeeTags,
  normalizeExpenseFeeTaxItems,
  normalizeExpenseFeeCurrencyOptions,
  resolveExpenseFeeFolio,
  resolveExpenseFeeHasPdf,
  resolveExpenseFeeHasReceipt,
  resolveExpenseFeeHasXml,
  resolveExpenseFeeReference,
  resolveExpenseFeeSaleName,
  resolveExpenseFeeStatus,
  sortExpenseFeeRowsByDateAsc,
  toPositiveNumber,
  toSafeText,
} from '@/composables/expensesFees/shared/useExpenseFeeListShared';
import { FEES_TEXT } from '@/texts/catalog/fees';

const LIST_TEXT = FEES_TEXT.list;

const resolveStatus = (row) => {
  return resolveExpenseFeeStatus(row, {
    paidLabel: LIST_TEXT.status.paid,
    pendingLabel: LIST_TEXT.status.pending,
  });
};

const mapExpenseRow = (row = {}) => {
  const provider = row?.provider && typeof row.provider === 'object' ? row.provider : (row?.supplier || {});
  const collaborator = row?.collaborator && typeof row.collaborator === 'object' ? row.collaborator : {};
  const commissionAgent = row?.commission_agent && typeof row.commission_agent === 'object' ? row.commission_agent : {};
  const expenseClass = row?.class && typeof row.class === 'object' ? row.class : {};
  const category = row?.category && typeof row.category === 'object' ? row.category : {};
  const customer = row?.company && typeof row.company === 'object' ? row.company : {};

  const providerFallback = typeof row?.provider === 'string' ? row.provider : '';
  const collaboratorFallback = typeof row?.collaborator === 'string' ? row.collaborator : '';
  const classFallback = typeof row?.expense_class === 'string' ? row.expense_class : '';
  const categoryFallback = typeof row?.category === 'string' ? row.category : '';
  const customerFallback = typeof row?.customer === 'string' ? row.customer : '';
  const areaFallback = typeof row?.area === 'string' ? row.area : '';

  const providerId = toPositiveNumber(provider.id || row?.provider_id);
  const collaboratorId = toPositiveNumber(collaborator.id || row?.collaborator_id || commissionAgent?.id);
  const expenseClassId = toPositiveNumber(expenseClass.id || row?.class_id);
  const categoryId = toPositiveNumber(category.id || row?.category_id);
  const customerId = toPositiveNumber(customer.id || row?.company_id || row?.customer_id);
  const saleId = toPositiveNumber(row?.sale_id || row?.sale?.id);
  const paymentId = toPositiveNumber(row?.payment_id || row?.payment?.id);

  const tags = normalizeExpenseFeeTags(row?.tags);
  const createdAt = toSafeText(row?.created_at || row?.date || '');
  const taxes = row?.taxes && typeof row.taxes === 'object' ? row.taxes : {};
  const taxItems = normalizeExpenseFeeTaxItems(taxes?.items);
  const transferredTaxes = Number(taxes?.transferred_total ?? row?.transfered_taxes ?? row?.transferred_taxes ?? 0) || 0;
  const withheldTaxes = Number(taxes?.withheld_total ?? row?.withheld_taxes ?? 0) || 0;
  const totalAmount = Number(row?.total ?? row?.amount ?? 0) || 0;
  const subtotalAmount = Number(row?.subtotal ?? (totalAmount - transferredTaxes + withheldTaxes) ?? 0) || 0;

  const collaboratorName = toSafeText(
    collaborator.name
    || row?.collaborator_name
    || collaboratorFallback
    || (commissionAgent?.type === 'collaborator' ? commissionAgent?.name : '')
  );

  const resolvedCommissionAgentName = toSafeText(
    row?.commission_agent_name
    || commissionAgent?.name
    || collaboratorName
    || provider.name
    || row?.provider_name
    || row?.supplier_name
    || providerFallback
  );

  const resolvedCommissionAgentType = toSafeText(
    row?.commission_agent_type
    || commissionAgent?.type
    || (collaboratorId > 0 ? 'collaborator' : 'supplier')
  );

  return {
    id: toPositiveNumber(row?.id),
    date: toSafeText(row?.date),
    provider: toSafeText(provider.name || row?.provider_name || row?.supplier_name || providerFallback || collaboratorName),
    provider_id: providerId,
    collaborator: collaboratorName,
    collaborator_id: collaboratorId,
    commission_agent_name: resolvedCommissionAgentName,
    commission_agent_type: resolvedCommissionAgentType,
    expense_class: toSafeText(expenseClass.name || row?.class_name || classFallback),
    expense_class_id: expenseClassId,
    category: toSafeText(category.name || row?.category_name || categoryFallback),
    category_id: categoryId,
    concept: toSafeText(row?.concept),
    sale_id: saleId > 0 ? saleId : null,
    sale_name: resolveExpenseFeeSaleName(row),
    sale_code: toSafeText(row?.sale_code || row?.sale?.code || ''),
    amount: totalAmount,
    subtotal: subtotalAmount,
    total: totalAmount,
    taxes: {
      transferred_total: transferredTaxes,
      withheld_total: withheldTaxes,
      net_total: transferredTaxes - withheldTaxes,
      items: taxItems,
    },
    exchange_rate: Number(row?.exchange_rate || row?.type_change || 0) || 0,
    payment_date: toSafeText(row?.payment_date),
    payment_id: paymentId > 0 ? paymentId : null,
    is_approved: row?.is_approved,
    voucher_type: toSafeText(row?.voucher_type || row?.voucherType).toLowerCase(),
    voucher_type_label: toSafeText(
      row?.voucher_type_label
      || row?.voucher_type_name
      || row?.tipo_de_comprobante
    ),
    special: Number(row?.special || 0) === 1 ? 1 : 0,
    status_code: toSafeText(row?.status_code).toUpperCase(),
    status: resolveStatus(row),
    folio: resolveExpenseFeeFolio(row),
    reference: resolveExpenseFeeReference(row),
    customer: toSafeText(customer.name || row?.customer_name || customerFallback),
    customer_id: customerId,
    tags: tags.map((tag) => tag.name),
    tag_ids: tags.map((tag) => tag.id).filter((id) => id > 0),
    has_xml: resolveExpenseFeeHasXml(row),
    has_pdf: resolveExpenseFeeHasPdf(row),
    has_receipt: resolveExpenseFeeHasReceipt(row),
    currency: toSafeText(row?.currency).toUpperCase(),
    area: toSafeText(row?.area?.name || row?.area_name || areaFallback),
    area_id: toPositiveNumber(provider.area_id || row?.area_id || row?.area?.id),
    created_at: createdAt === '' ? null : createdAt,
  };
};

export const getFeesFilterOptions = () => {
  return {
    provider: [],
    collaborator: [],
    expense_class: [],
    category: [],
    customer: [],
    tags: [],
    has_xml: buildExpenseFeeHasXmlOptions({
      yesLabel: LIST_TEXT.hasXml.yes,
      noLabel: LIST_TEXT.hasXml.no,
    }),
    currency: [],
    area: [],
  };
};

const normalizeSelectorOptions = (items) => {
  return normalizeCatalogOptions(items, { useCodeInLabel: false });
};

const extractSnapshotMetrics = (response) => {
  const payload = extractPayload(response);
  const metrics = payload && typeof payload === 'object'
    ? payload.metrics
    : null;

  return metrics && typeof metrics === 'object' ? metrics : {};
};

export const fetchFeesFilterOptions = async () => {
  const requests = [
    ['provider', suppliersService.selectors()],
    ['collaborator', collaboratorsService.selectors()],
    ['customer', customersService.selectors()],
    ['area', areasService.index()],
    ['tags', tagsService.index()],
    ['expense_class', expenseClassesService.selectors()],
    ['category', expenseCategoriesService.selectors()],
    ['currency', currenciesService.selectors()],
  ];

  const results = await Promise.allSettled(requests.map(([, request]) => request));

  const collections = {};

  requests.forEach(([key], index) => {
    const result = results[index];
    collections[key] = result?.status === 'fulfilled'
      ? extractCollection(result.value)
      : [];
  });

  return {
    provider: normalizeSelectorOptions(collections.provider),
    collaborator: normalizeSelectorOptions(collections.collaborator),
    expense_class: normalizeSelectorOptions(collections.expense_class),
    category: normalizeSelectorOptions(collections.category),
    customer: normalizeSelectorOptions(collections.customer),
    tags: normalizeSelectorOptions(collections.tags),
    has_xml: buildExpenseFeeHasXmlOptions({
      yesLabel: LIST_TEXT.hasXml.yes,
      noLabel: LIST_TEXT.hasXml.no,
    }),
    currency: normalizeExpenseFeeCurrencyOptions(collections.currency),
    area: normalizeSelectorOptions(collections.area),
  };
};

export const fetchFeesRows = async (params = {}) => {
  const snapshot = await fetchFeesSnapshot(params);

  return Array.isArray(snapshot?.rows) ? snapshot.rows : [];
};

export const fetchFeesSnapshot = async (params = {}) => {
  const response = await feesService.index(params);
  const rows = extractCollection(response);
  const normalizedRows = (Array.isArray(rows) ? rows : []).map((row) => mapExpenseRow(row));

  return {
    rows: sortExpenseFeeRowsByDateAsc(normalizedRows),
    metrics: extractSnapshotMetrics(response),
  };
};
